import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import AdminSidebar from './AdminSidebar';
import AdminTopbar from './AdminTopbar';
import { IS_CORP } from '@/theme/adminTheme';
import '@/styles/adminShell.css';

/* The admin page shell: sidebar + top bar + page header + content container.
   ---------------------------------------------------------------------------
   Every /admin page used to assemble this by hand, and no two did it the same
   way — different wrapper classes, different top bars, different loading
   states, and only Admin.tsx bothered with the RTL margin flip. Pages now say
   what they ARE (section, title, blurb, actions) and this decides how it looks.

   `.adminx` is applied here rather than per page, so the accent tokens in
   adminShell.css and the Poppins stack reach the top bar as well as the body. */

export interface AdminLayoutProps {
  /** Breadcrumb parent — the sidebar section this page sits under. */
  section?: string;
  /** Page name: the breadcrumb leaf and, unless `hideHeader`, the <h1>. */
  title: string;
  /** One-line explanation under the title. */
  subtitle?: ReactNode;
  /** Icon shown beside the title. */
  icon?: LucideIcon;
  /** Controls in the top bar, before the profile menu (search, save, …). */
  topbarActions?: ReactNode;
  /** Controls on the right of the page header. */
  actions?: ReactNode;
  /**
   * Skip the standard page header — for pages that open with their own hero
   * banner (Overview, Accounting) or a custom header row.
   */
  hideHeader?: boolean;
  /** Render the shared loading state instead of children. */
  loading?: boolean;
  /** Message shown with the loading spinner. */
  loadingLabel?: string;
  /** Cap the content at a reading width instead of the full 1760px. */
  narrow?: boolean;
  /**
   * Space the children vertically — for pages that are a stack of editor cards
   * rather than a single grid. Pages set their own gaps before this, and used
   * three different values for the same look.
   */
  spaced?: boolean;
  /** Full-bleed content placed between the top bar and the page container. */
  banner?: ReactNode;
  children?: ReactNode;
}

export default function AdminLayout({
  section,
  title,
  subtitle,
  icon: Icon,
  topbarActions,
  actions,
  hideHeader,
  loading,
  loadingLabel = 'Loading…',
  narrow,
  spaced,
  banner,
  children,
}: AdminLayoutProps) {
  return (
    <div className="ax-shell">
      <AdminSidebar />

      <main className={`ax-main adminx${IS_CORP ? ' is-corp' : ''}`}>
        <AdminTopbar section={section} title={title} actions={topbarActions} />

        {banner}

        {loading ? (
          <div className="ax-loading">
            <Loader2 className="spin" />
            <span>{loadingLabel}</span>
          </div>
        ) : (
          <div className={`ax-page${narrow ? ' ax-page-narrow' : ''}`}>
            {!hideHeader && (
              <div className="ax-ph">
                <div className="ax-ph-text">
                  <h1>{Icon && <Icon />}{title}</h1>
                  {subtitle && <p>{subtitle}</p>}
                </div>
                {actions && <div className="ax-ph-actions">{actions}</div>}
              </div>
            )}
            {spaced ? <div className="ax-stack">{children}</div> : children}
          </div>
        )}
      </main>
    </div>
  );
}
